import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, ArrowUpRight, Check } from "lucide-react";
import PageShell from "./PageShell";
import Seo from "../components/Seo";
import { Reveal, useEnquiry } from "../lib/site-ui";
import {
  PRICING, COURSES, MEDIA, CONTACT, WHATSAPP_URL, TEL_URL, SEO_LINKS,
  ORG_JSONLD, breadcrumbJsonLd,
} from "../config/site";

const link = "text-white underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors";

const points = [
  ["Who it's for", "Aspiring pilots anywhere in India who want structured DGCA ground preparation without relocating — from students in smaller towns to those balancing college or work."],
  ["How the online classes work", "Classes are live and interactive, not pre-recorded. You join from home, follow along in real time, ask questions during the session, and get the same concept-first teaching as our in-person students."],
  ["Doubt support & personalised guidance", "Learning online should not mean learning alone. You get direct access to the instructor for doubt-solving and a preparation approach adapted to the subjects you find most challenging."],
];

function Body() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative isolate min-h-[70svh] flex items-end overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={MEDIA.hero} alt="Online DGCA ground classes across India with AeroVaid Aviation Academy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/55" />
        </div>
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-8 pb-16 pt-32">
          <Reveal as="p" className="label-mono text-slate-400 mb-5">Live Online · Across India</Reveal>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-3xl leading-[1.04]">
            Online DGCA Ground Classes Across India
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Live, interactive DGCA ground coaching you can join from anywhere in India — with real doubt support and
            personalised guidance from the instructor.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button onClick={() => openEnquiry({ mode: "Online (Across India)" })} data-testid="online-demo-btn"
              className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide">
              BOOK YOUR FREE DEMO CLASS
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
              className="border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide flex items-center justify-center gap-2">
              <MessageCircle className="w-4 h-4" /> WHATSAPP US
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 space-y-14">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">Learn with AeroVaid from anywhere</h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              AeroVaid Aviation Academy is based in Mumbai but teaches aspiring pilots across India through live online
              DGCA ground classes. If you are in the city, you can also explore{" "}
              <Link to={SEO_LINKS.mumbai} className={link}>personalised DGCA ground classes in Mumbai</Link>. Either
              way, the teaching stays concept-based and focused on DGCA examination preparation.
            </p>
          </Reveal>

          {points.map(([h, p]) => (
            <Reveal key={h}>
              <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">{h}</h2>
              <p className="text-slate-300 text-lg leading-relaxed">{p}</p>
            </Reveal>
          ))}

          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-6">Subjects covered</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {COURSES.map((c) => (
                <div key={c.id} className="flex items-center gap-3 text-slate-200">
                  <Check className="w-4 h-4 text-slate-500 shrink-0" /> {c.name}
                </div>
              ))}
            </div>
            <p className="mt-6 text-slate-400">
              Explore each of the <Link to="/#courses" className={link}>DGCA subjects</Link> and our{" "}
              <Link to="/#pricing" className={link}>founding-batch pricing</Link>.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">Simple, transparent pricing</h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Founding-batch pricing is{" "}
              <span className="text-white font-semibold">{PRICING.currency}{PRICING.amount} {PRICING.unit}</span>.
              {" "}{PRICING.tagline}
            </p>
          </Reveal>

          <Reveal>
            <div className="border border-white/10 bg-slate-900/40 p-8 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-4">Start with a free demo</h2>
              <p className="text-slate-300 leading-relaxed mb-7">
                Attend a free online demo class, meet the instructor, and see the teaching approach before you enrol.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => openEnquiry({ mode: "Online (Across India)" })}
                  className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide">
                  BOOK FREE DEMO
                </button>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-8 py-4 rounded-sm hover:bg-[#20bd5a] transition-colors text-sm tracking-wide">
                  <MessageCircle className="w-4 h-4" /> WHATSAPP
                </a>
                <a href={TEL_URL}
                  className="flex items-center justify-center gap-2 border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide">
                  <Phone className="w-4 h-4" /> CALL {CONTACT.phoneDisplay}
                </a>
              </div>
              <p className="mt-8 text-slate-400">
                <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                  Back to AeroVaid home <ArrowUpRight className="w-4 h-4" />
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default function OnlinePage() {
  const jsonLd = useMemo(() => [
    ORG_JSONLD,
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Online DGCA Ground Classes in India", path: SEO_LINKS.online },
    ]),
  ], []);
  return (
    <PageShell>
      <Seo
        title="Online DGCA Ground Classes in India | AeroVaid"
        description="Live online DGCA ground classes for aspiring pilots across India. Interactive coaching, real doubt support and concept-based DGCA exam preparation. Book a free demo."
        path={SEO_LINKS.online}
        jsonLd={jsonLd}
      />
      <Body />
    </PageShell>
  );
}
